import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getUserDisplayName, getUserEmail } from '../utils/auth';
import { formatRepairNumber } from '../utils/format';

const API_BASE = 'https://vhnsv33dvk.execute-api.eu-west-1.amazonaws.com';

const STATUS_LABELS = {
  initial_inspection:  'Initial Inspection',
  estimate_pending:    'Estimate Pending',
  estimate_sent:       'Estimate Shared',
  estimate_approved:   'Estimate Approved',
  waiting_parts:       'Waiting Parts',
  in_progress:         'In Progress',
  on_test:             'On Test',
  completed:           'Ready for Collection',
  collected:           'Collected',
  cancelled:           'Cancelled',
  returned:            'Returned',
  return_in_progress:  'Return In Progress',
  no_repair:           'No Repair'
};

const STATUS_COLOURS = {
  initial_inspection:  '#95a5a6',
  estimate_pending:    '#e67e22',
  estimate_sent:       '#f39c12',
  estimate_approved:   '#27ae60',
  waiting_parts:       '#8e44ad',
  in_progress:         '#2980b9',
  on_test:             '#16a085',
  completed:           '#27ae60',
  collected:           '#7f8c8d',
  cancelled:           '#c0392b',
  returned:            '#e74c3c',
  return_in_progress:  '#c0392b',
  no_repair:           '#7f8c8d'
};

const PENDING_STATUSES = [
  'initial_inspection', 'estimate_pending', 'estimate_sent', 'estimate_approved'
];

const IN_PROGRESS_STATUSES = [
  'waiting_parts', 'in_progress', 'on_test', 'returned', 'return_in_progress'
];

const COLLECTION_STATUSES = [
  'completed', 'no_repair'
];

const ITEM_TYPES = ['watch', 'clock', 'jewellery', 'other'];

function itemTypeFilterStorageKey(email) {
  return `repair-shop:item-type-filter:${email || 'default'}`;
}

function loadItemTypeFilter(email) {
  try {
    const raw = localStorage.getItem(itemTypeFilterStorageKey(email));
    if (!raw) return ITEM_TYPES;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return ITEM_TYPES;
    // Drop anything that isn't a known item type - guards against stale
    // or corrupted data left over from a previous version of this list.
    const valid = parsed.filter(t => ITEM_TYPES.includes(t));
    // If everything was filtered out (e.g. the user had genuinely
    // unchecked every box), fall back to showing all types rather than
    // silently showing nothing on their next visit.
    return valid.length > 0 ? valid : ITEM_TYPES;
  } catch (err) {
    // localStorage unavailable (private browsing, disabled, quota) -
    // fail open to the default rather than breaking the page.
    return ITEM_TYPES;
  }
}

function saveItemTypeFilter(email, filter) {
  try {
    localStorage.setItem(itemTypeFilterStorageKey(email), JSON.stringify(filter));
  } catch (err) {
    // Storage unavailable - the filter still works for this session via
    // React state, it just won't persist to the next visit.
  }
}

function Dashboard({ token, onLogout }) {
  const displayName = getUserDisplayName(token);
  const userEmail = getUserEmail(token);

  const handleLogoutClick = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      onLogout();
    }
  };
  const [repairs, setRepairs] = useState([]);
  const [tabCounts, setTabCounts] = useState({ all: 0, pending: 0, in_progress: 0, collection: 0 });  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const [itemTypeFilter, setItemTypeFilter] = useState(() => loadItemTypeFilter(userEmail));
  
  const toggleItemType = (type) => {
    setItemTypeFilter(prev => {
      const next = prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type];
      saveItemTypeFilter(userEmail, next);
      return next;
    });
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchRepairs(1);
    fetchCounts();
  }, [statusFilter]);

  useEffect(() => {
    fetchRepairs(currentPage);
  }, [currentPage]);

  const fetchCounts = async () => {
      try {
        const response = await apiFetch(`${API_BASE}/repairs/counts`, {}, onLogout);
        if (!response) return;
        const data = await response.json();
        setTabCounts(data.tab_counts || { all: 0, pending: 0, in_progress: 0, collection: 0 });
      } catch (err) {
        // non-critical, fail silently
      }
    };

  const fetchRepairs = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      let url;
      
      if (statusFilter === 'pending') {
        url = `${API_BASE}/repairs?statuses=${PENDING_STATUSES.join(',')}&page=${page}&page_size=20`;
      } else if (statusFilter === 'in_progress') {
        url = `${API_BASE}/repairs?statuses=${IN_PROGRESS_STATUSES.join(',')}&page=${page}&page_size=20`;
      } else if (statusFilter === 'collection') {
        url = `${API_BASE}/repairs?statuses=${COLLECTION_STATUSES.join(',')}&page=${page}&page_size=20`;
      } else {
        // All tab
        const backendFilter = 'all';
        url = `${API_BASE}/repairs?filter=${backendFilter}&page=${page}&page_size=20`;
      }

      const response = await apiFetch(url, {}, onLogout);
      if (!response) return;

      const data = await response.json();
      setRepairs(data.results || []);
      setTotalPages(data.total_pages || 1);
      setTotalCount(data.total_count || 0);
    } catch (err) {
      setError('Failed to load repairs.');
    } finally {
      setLoading(false);
    }
  };

  const filteredRepairs = repairs.filter(r => itemTypeFilter.includes(r.item_type));

  const statusCounts = repairs.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="portal">
      <nav className="portal-nav">
        <button className="portal-nav-brand" onClick={() => navigate('/admin')}>Repair Shop</button>
        <div className="portal-nav-links">
          <button className="nav-btn active">Dashboard</button>
          <button className="nav-btn" onClick={() => navigate('/admin/search')}>Search</button>
          <button className="nav-btn" onClick={() => navigate('/admin/repairs/new')}>New Repair</button>
          <button className="nav-btn logout" onClick={handleLogoutClick}>{displayName || 'Logout'}</button>
        </div>
      </nav>

      <div className="portal-content">
        <div className="portal-header">
          <h1>Dashboard</h1>
          <button className="print-btn" onClick={() => window.print()}>🖨 Print List</button>
        </div>
        
        <div className="filter-tabs">
          <button
            className={`filter-tab ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All {tabCounts.all > 0 && `(${tabCounts.all})`}
          </button>
          <button
            className={`filter-tab ${statusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => setStatusFilter('pending')}
          >
            Pending Start {tabCounts.pending > 0 && `(${tabCounts.pending})`}
          </button>
          <button
            className={`filter-tab ${statusFilter === 'in_progress' ? 'active' : ''}`}
            onClick={() => setStatusFilter('in_progress')}
          >
            In Progress {tabCounts.in_progress > 0 && `(${tabCounts.in_progress})`}
          </button>
          <button
            className={`filter-tab ${statusFilter === 'collection' ? 'active' : ''}`}
            onClick={() => setStatusFilter('collection')}
          >
            Ready for Collection {tabCounts.collection > 0 && `(${tabCounts.collection})`}
          </button>
        </div>

        <div className="item-type-filter">
          <span className="item-type-filter-label">Show:</span>
          {ITEM_TYPES.map(type => (
            <label key={type} className="item-type-checkbox">
              <input
                type="checkbox"
                checked={itemTypeFilter.includes(type)}
                onChange={() => toggleItemType(type)}
              />
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </label>
          ))}
        </div>

        <p className="dashboard-note">Items collected more than 5 days ago will not be displayed. Use the Search page to search for older repairs.</p>

        {loading && <p className="loading-text">Loading repairs...</p>}
        {error && <p className="error-text">{error}</p>}

        {!loading && !error && (
          <div className="repairs-list">
            {filteredRepairs.length === 0 ? (
              <p className="empty-text">No repairs found.</p>
            ) : (
              filteredRepairs.map(repair => (
                <div
                  key={repair.repair_number}
                  className="repair-card"
                  onClick={() => navigate(`/admin/repairs/${repair.repair_number}`)}
                >
                  <div className="repair-card-header">
                    <div className="repair-card-title">
                      <span className="repair-number">#{formatRepairNumber(repair.repair_number)}</span>
                      <span className="repair-customer">{repair.customer_name}</span>
                    </div>
                    <span
                      className="repair-status"
                      style={{ background: STATUS_COLOURS[repair.status] }}
                    >
                      {STATUS_LABELS[repair.status]}
                    </span>
                  </div>
                  <div className="repair-card-body">
                    <p className="repair-item">
                      {repair.item_type}{repair.item_description ? ` — ${repair.item_description}` : ''}
                    </p>
                    {repair.fault_reported && (
                      <p className="repair-fault">{repair.fault_reported}</p>
                    )}
                    {COLLECTION_STATUSES.includes(repair.status) && (
                      <p className="repair-call-indicator">
                        {repair.collection_called_at ? '📞 Called' : '⚠️ Not yet called'}
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(p => p - 1)}
              disabled={currentPage === 1}
            >
              ← Previous
            </button>
            <span className="pagination-info">
              Page {currentPage} of {totalPages} ({totalCount} repairs)
            </span>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(p => p + 1)}
              disabled={currentPage === totalPages}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;